import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Injector, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  UntypedFormControl,
  Validator,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash-es';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';

@Component({
  standalone: false,
  selector: 'nimbus-input-number',
  templateUrl: './input-number.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./input-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputNumberComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputNumberComponent),
      multi: true,
    },
  ],
})
export class InputNumberComponent extends IInputComponent<number> implements Validator {
  @Input()
  public hideClearButton: boolean = false;
  @Input()
  public showInfoIcon: boolean = false;
  @Input()
  public infoMessageSuffix: string;
  @Input()
  public infoMessage: string;
  @Input()
  public min: number;
  @Input()
  public max: number;
  @Input()
  public floatLabel: 'auto' | 'always' = 'auto';
  @Input()
  public showValidationOnInit: boolean = false;

  private get hasMin(): boolean {
    return !!this.min || this.min === 0;
  }

  private get hasMax(): boolean {
    return !!this.max || this.max === 0;
  }

  public get isClearButtonDisplayed(): boolean {
    return !this.hideClearButton && !this.isDisabled && !isNil(this.value);
  }

  public constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private injector: Injector,
    private readonly translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public isErrorState(_control?: UntypedFormControl | null, _form?: FormGroupDirective | NgForm | null): boolean {
    // If the flag is not enabled, use the default behavior from the base class
    if (!this.showValidationOnInit) {
      return super.isErrorState(_control, _form);
    }

    // Custom logic for showing validation errors immediately for pre-filled invalid values
    // Only active when showValidationOnInit is true
    if (this.ngControl && this.ngControl.invalid) {
      // Show error if touched or dirty (standard behavior)
      if (this.ngControl.dirty || this.ngControl.touched) {
        return true;
      }

      // Also show error if the value is invalid and the control was initialized with a value
      // This handles the case where a form is pre-filled with invalid data
      if (this.ngControl.value !== null && this.ngControl.value !== undefined) {
        return true;
      }
    }

    return false;
  }

  public onClearValueClick(event: Event): void {
    event.stopPropagation();
    this.value = undefined;
  }

  public onValueIncDecClick(value: number): void {
    if (!this.isDisabled) {
      let result = !this.value ? value : this.value + value;
      result = this.hasMin && result < this.min ? this.min : result;
      result = this.hasMax && result > this.max ? this.max : result;
      this.value = result;
    }
  }

  public validate(control: AbstractControl): { [key: string]: unknown } {
    if ((!control || !control?.value) && control?.value !== 0) {
      return null;
    }
    if (this.hasMin && control.value < this.min) {
      return { min: { min: this.min } };
    }
    if (this.hasMax && control.value > this.max) {
      return { max: { max: this.max } };
    }
    return null;
  }
}
